import { Filter } from "@/shared/lib/filter/filter";
import { Address } from "@/services/backend/addresses/types/address";
import { Representative } from "@/services/backend/representatives/types/representative";
import { Contact } from "@/services/backend/contacts/types/contact";
import {
  DocumentBaseFilters,
  DocumentCommonFilters,
} from "@/services/backend/documents/types/filters";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { FulfillmentState } from "@/services/backend/relationships/types/fulfillment";
import { downloadResponseHandler } from "@/services/backend/_lib/pdf-helpers";
import {
  objectURLResponseHandler,
  revokeObjectURLOptions,
} from "@/shared/lib/utilities/object-url";
import { backendApi } from "@/services/backend/api";
import { updateCache } from "@/services/backend/_lib/update-cache";
import {
  ReferenceNumberFilters,
  ResetReferenceNumberRequest,
} from "@/services/backend/documents/types/reference-number";
import { WorkOrder } from "@/services/backend/rlt/work-orders/types/work-order";
import { Lst } from "@/shared/lib/lst/lst";
import { DocumentState } from "@/services/backend/documents/types/state";

const rltWorkOrdersApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    rltWorkOrderCreate: build.mutation<WorkOrder, CreateRequest>({
      query: (request) => ({
        url: `/rlt/work-order`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltWorkOrder", id: request.id },
        "RltWorkOrder",
      ],
    }),
    rltWorkOrderShow: build.query<WorkOrder, ShowRequest>({
      query: (request) => ({
        url: `/rlt/work-order`,
        method: "GET",
        params: request,
      }),
      providesTags: (result) =>
        result ? [{ type: "RltWorkOrder", id: result.id }] : [],
    }),
    rltWorkOrderPatch: build.mutation<WorkOrder, PatchRequest>({
      query: (request) => ({
        url: `/rlt/work-order`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltWorkOrder", id: request.id },
        { type: "RltWorkOrder", id: "LIST" },
      ],
    }),
    rltWorkOrderResetReferenceNumber: build.mutation<
      WorkOrder,
      ResetReferenceNumberRequest
    >({
      query: (request) => ({
        url: `/rlt/work-order/reset-reference-number`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltWorkOrder", id: request.id },
        { type: "RltWorkOrder", id: "LIST" },
      ],
    }),
    rltWorkOrderList: build.query<Lst<WorkOrder>, ListRequest>({
      query: (request) => ({
        url: `/rlt/work-order/list`,
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) =>
        result ? [{ type: "RltWorkOrder", id: "LIST" }] : [],
    }),
    rltWorkOrderUpdateWorkOrderState: build.mutation<
      WorkOrder,
      UpdateWorkOrderStateRequest
    >({
      query: (request) => ({
        url: `/rlt/work-order/work-order-state`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltWorkOrder", id: request.id },
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltWorkOrderUpdateExecutingBranch: build.mutation<
      WorkOrder,
      UpdateExecutingBranchRequest
    >({
      query: (request) => ({
        url: `/rlt/work-order/executing-branch`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltWorkOrder", id: request.id },
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltWorkOrderUpdateBeginDate: build.mutation<
      WorkOrder,
      UpdateBeginDateRequest
    >({
      query: (request) => ({
        url: `/rlt/work-order/begin-date`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltWorkOrder", id: request.id },
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltWorkOrderUpdateEndDate: build.mutation<WorkOrder, UpdateEndDateRequest>({
      query: (request) => ({
        url: `/rlt/work-order/end-date`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltWorkOrder", id: request.id },
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltWorkOrderUpdateTeamLeader: build.mutation<
      WorkOrder,
      UpdateTeamLeaderRequest
    >({
      query: (request) => ({
        url: `/rlt/work-order/team-leader`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltWorkOrder", id: request.id },
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltWorkOrderUpdateCustomerReference: build.mutation<
      WorkOrder,
      UpdateCustomerReferenceRequest
    >({
      query: (request) => ({
        url: `/rlt/work-order/customer-reference`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (result, e, request) => [
        { type: "RltWorkOrder", id: request.id },
        "RltWorkOrder",
      ],
    }),
    rltWorkOrderUpdateInternalComment: build.mutation<
      WorkOrder,
      UpdateInternalCommentRequest
    >({
      query: (request) => ({
        url: `/rlt/work-order/internal-comment`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltWorkOrder", id: request.id },
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltWorkOrderUpdateServiceAddress: build.mutation<
      WorkOrder,
      UpdateServiceAddressRequest
    >({
      query: (request) => ({
        url: `/rlt/work-order/service-address`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltWorkOrder", id: request.id },
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltWorkOrderUpdateServiceContact: build.mutation<
      WorkOrder,
      UpdateServiceContactRequest
    >({
      query: (request) => ({
        url: `/rlt/work-order/service-contact`,
        method: "PATCH",
        body: {
          id: request.id,
          serviceContact: request.contact,
        },
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltWorkOrder", id: request.id },
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltWorkOrderUpdateRepresentative: build.mutation<
      WorkOrder,
      UpdateRepresentativeRequest
    >({
      query: (request) => ({
        url: `/rlt/work-order/representative`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltWorkOrder", id: request.id },
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltWorkOrderSign: build.mutation<WorkOrder, SignRequest>({
      query: (request) => {
        const body = new FormData();
        body.append("file", request.signature);
        return {
          url: `/rlt/work-order/sign`,
          method: "PATCH",
          headers: {
            //  this is needed, otherwise a 415 error is returned
            Accept: "application/json",
          },
          params: {
            id: request.id,
            dataFormat: request.dataFormat,
            nameOfSigner: request.nameOfSigner,
          },
          body,
          formData: true,
        };
      },
      invalidatesTags: (_result, _e, request) => [
        { type: "RltWorkOrder", id: request.id },
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltWorkOrderPreviewPDF: build.query<
      { objectURL: string },
      PreviewPDFRequest
    >({
      ...revokeObjectURLOptions(),
      query: (request) => ({
        url: `/rlt/work-order`,
        method: "GET",
        params: request,
        headers: { Accept: "application/pdf" },
        responseHandler: objectURLResponseHandler,
        // Keep the PDF out of the browser's HTTP cache.
        cache: "no-store",
      }),
    }),
    rltWorkOrderDownloadPDF: build.mutation<
      { filename: string; objectURL: string },
      PreviewPDFRequest
    >({
      query: (request) => ({
        url: `/rlt/work-order`,
        method: "GET",
        params: { ...request, download: true },
        headers: { Accept: "application/pdf" },
        responseHandler: downloadResponseHandler,
        // Keep the PDF out of the browser's HTTP cache.
        cache: "no-store",
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltWorkOrder", id: request.id },
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltWorkOrderUpdateState: build.mutation<WorkOrder, UpdateStateRequest>({
      query: (request) => ({
        url: `/rlt/work-order/state`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltWorkOrder", id: request.id },
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    rltWorkOrderDelete: build.mutation<unknown, DeleteRequest>({
      query: (request) => ({
        url: `/rlt/work-order`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltWorkOrder", id: request.id },
        "RltWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
  }),
});

export const {
  useRltWorkOrderCreateMutation,
  useRltWorkOrderShowQuery,
  useRltWorkOrderPatchMutation,
  useRltWorkOrderResetReferenceNumberMutation,
  useRltWorkOrderListQuery,
  useRltWorkOrderUpdateWorkOrderStateMutation,
  useRltWorkOrderUpdateExecutingBranchMutation,
  useRltWorkOrderUpdateBeginDateMutation,
  useRltWorkOrderUpdateEndDateMutation,
  useRltWorkOrderUpdateTeamLeaderMutation,
  useRltWorkOrderUpdateCustomerReferenceMutation,
  useRltWorkOrderUpdateInternalCommentMutation,
  useRltWorkOrderUpdateServiceAddressMutation,
  useRltWorkOrderUpdateServiceContactMutation,
  useRltWorkOrderUpdateRepresentativeMutation,
  useRltWorkOrderSignMutation,
  useRltWorkOrderPreviewPDFQuery,
  useLazyRltWorkOrderPreviewPDFQuery,
  useRltWorkOrderDownloadPDFMutation,
  useRltWorkOrderUpdateStateMutation,
  useRltWorkOrderDeleteMutation,
} = rltWorkOrdersApi;

// PatchRequest updates the document. All fields except `id` are optional:
// an omitted key leaves the corresponding value unchanged.
export interface PatchRequest {
  id: string;
  referenceNumber?: string;
}

export interface CreateRequest {
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

export interface ShowRequest {
  id: string;
}

export interface ListRequest
  extends DocumentBaseFilters, DocumentCommonFilters, ReferenceNumberFilters {
  orderBy?: OrdrBy<WorkOrderOrderBy>[];
  workOrderState?: Filter<FulfillmentState>;
  executingBranch?: Filter<string>;
  beginDate?: Filter<string>;
  endDate?: Filter<string>;
  state?: Filter<DocumentState>;
  limit?: number;
  offset?: number;
}

export type WorkOrderOrderBy =
  | "beginDate"
  | "endDate"
  | "stateSetAt"
  | "percentageCompleted"
  | "workOrderState"
  | "teamLeader"
  | "createdAt"
  | "updatedAt";

export interface UpdateWorkOrderStateRequest {
  id: string;
  workOrderState: FulfillmentState;
  /**
   * The user requesting the transition. The document state the new
   * workOrderState implies is recorded for them, and the backend policy
   * rejects any other user.
   */
  stateSetBy: string;
}

export interface UpdateExecutingBranchRequest {
  id: string;
  executingBranchId: string;
}

export interface UpdateBeginDateRequest {
  id: string;
  beginDate: string;
}

export interface UpdateEndDateRequest {
  id: string;
  // null clears the end date.
  endDate: string | null;
}

export interface UpdateTeamLeaderRequest {
  id: string;
  teamLeaderId: string;
}

export interface UpdateCustomerReferenceRequest {
  id: string;
  customerReference: string;
}

export interface UpdateInternalCommentRequest {
  id: string;
  internalComment: string;
}

export interface UpdateServiceAddressRequest {
  id: string;
  serviceAddress: Address;
}

export interface UpdateServiceContactRequest {
  id: string;
  // naming is contact and not serviceContact because
  // with "contact" the ContactCardWithMutation component
  // can be used. The conflict with the expected naming
  // "serviceContact" in the backend is resolved in the
  // mutation definition above.
  contact: Contact;
}

export interface UpdateRepresentativeRequest {
  id: string;
  representative: Representative;
}

export interface SignRequest {
  id: string;
  signature: string;
  dataFormat: string;
  nameOfSigner: string;
}

export interface PreviewPDFRequest {
  id: string;
  document: WorkOrderDocument;
}

export enum WorkOrderDocument {
  All = "all",
  WorkOrder = "work_order",
  LastMinuteRiskAnalysis = "lmra",

  Documentation = "documentation",
}

export interface UpdateStateRequest {
  id: string;
  state: DocumentState;
  stateSetBy: string;
}

export interface DeleteRequest {
  id: string;
}

export const updateRltWorkOrderCache = updateCache(
  rltWorkOrdersApi,
  "rltWorkOrderShow",
);
